/**
 * Transfer Request service — coordinated FHIR writes for the transfer workflow.
 *
 * A transfer performs three operations on existing resources (PRD):
 *  1. Update ServiceRequest tracker stage from `fulfilled` to `transfer`
 *  2. Create a QuestionnaireResponse with transfer context
 *  3. Update ServiceRequest preference extensions
 *
 * See `docs/prd/Tasks/PRD_TRANSFER_REQUESTS_FHIR_MAPPING.md`.
 */

import type { MedplumClient } from '@medplum/core';
import type {
  Extension,
  Questionnaire,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  ServiceRequest,
} from '@medplum/fhirtypes';
import { displayLabelForEpisodeType } from './fhirValueSets/sessionAndEpisodeTypes';
import {
  AI_TRANSFER_SUMMARY_EXT,
  DETAILED_STATUS_EXT,
  EPISODE_OF_CARE_CONTEXT_EXT,
  TEMPLATE_CATEGORY_SYSTEM,
} from '@unify/fhir-model/constants';
import {
  setTrackerStage,
  getTrackerStage,
  setSessionFormatPreference,
  setAvailabilityPreference,
  setAreasOfFocus,
  setProviderPreferences,
  getEpisodeOfCareRef,
  getSessionFormatPreference as srGetSessionFormatPreference,
  getAvailabilityPreference as srGetAvailabilityPreference,
  getAreasOfFocus as srGetAreasOfFocus,
  getProviderPreferences as srGetProviderPreferences,
  type SessionFormatPreference,
} from '@unify/fhir-model/resources/service-request';
import {
  TRANSFER_TEMPLATE_CATEGORY,
  TRANSFER_TEMPLATE_CATEGORY_TAG,
  TRANSFER_QR_LINK_IDS,
  type TransferReasonCode,
} from './transferFhirConstants';
import { getPatientAccountRefs } from './clientFacingTasks';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AiSummaryMetadata {
  model: string;
  generatedAt: string;
  sourceCounts: {
    encounters: number;
    notes: number;
    questionnaireResponses: number;
    treatmentPlanningDocs: number;
  };
}

export interface SubmitTransferRequestInput {
  serviceRequestId: string;
  episodeOfCareId: string;
  patient: { id: string; display?: string };
  author: { id: string; display?: string };
  transferReasons: TransferReasonCode[];
  transferExplanation: string;
  recommendedClinicians?: string;
  sessionFormatPreference?: SessionFormatPreference;
  availabilityPreference?: string[];
  areasOfFocus?: string[];
  providerPreferences?: string[];
  aiSummaryMetadata?: AiSummaryMetadata;
}

export interface SubmitTransferResult {
  serviceRequest: ServiceRequest;
  questionnaireResponse: QuestionnaireResponse;
}

// ---------------------------------------------------------------------------
// Questionnaire template lookup
// ---------------------------------------------------------------------------

/**
 * Find the transfer context Questionnaire template by its category tag.
 * Returns the first active template tagged `transfers`.
 */
export async function getTransferQuestionnaireTemplate(
  medplum: MedplumClient,
): Promise<Questionnaire | undefined> {
  const results = await medplum.searchResources('Questionnaire', {
    _tag: TRANSFER_TEMPLATE_CATEGORY_TAG,
    status: 'active',
    _count: '1',
    _sort: '-_lastUpdated',
  });
  return results[0];
}

// ---------------------------------------------------------------------------
// QR query for an episode
// ---------------------------------------------------------------------------

function episodeReference(episodeOfCareId: string): string {
  return `EpisodeOfCare/${episodeOfCareId}`;
}

async function resolvePatientIdForEpisode(
  medplum: MedplumClient,
  episodeOfCareId: string,
  patientId?: string,
): Promise<string | undefined> {
  if (patientId?.trim()) {
    return patientId.trim();
  }
  const eoc = await medplum.readResource('EpisodeOfCare', episodeOfCareId);
  return eoc.patient?.reference?.replace(/^Patient\//, '');
}

function getQuestionnaireResponseEpisodeRef(
  qr: QuestionnaireResponse,
): string | undefined {
  return qr.extension?.find((e) => e.url === EPISODE_OF_CARE_CONTEXT_EXT)
    ?.valueReference?.reference;
}

/**
 * Fetch the transfer context QuestionnaireResponse for a given EpisodeOfCare.
 * Medplum does not index `extension:EpisodeOfCare` on QuestionnaireResponse — search by
 * patient + transfers tag, then match the episode-of-care-context extension client-side.
 */
export async function getTransferQRForEpisode(
  medplum: MedplumClient,
  episodeId: string,
  opts?: { patientId?: string },
): Promise<QuestionnaireResponse | undefined> {
  const patientId = await resolvePatientIdForEpisode(medplum, episodeId, opts?.patientId);
  if (!patientId) {
    return undefined;
  }

  const targetRef = episodeReference(episodeId);
  const results = await medplum.searchResources('QuestionnaireResponse', {
    subject: `Patient/${patientId}`,
    _tag: TRANSFER_TEMPLATE_CATEGORY_TAG,
    _count: '20',
    _sort: '-_lastUpdated',
  });
  return results.find((qr) => getQuestionnaireResponseEpisodeRef(qr) === targetRef);
}

// ---------------------------------------------------------------------------
// QR builder
// ---------------------------------------------------------------------------

export interface BuildTransferQRInput {
  questionnaireRef: string;
  patient: { id: string; display?: string };
  author: { id: string; display?: string };
  episodeOfCare: { id: string; display?: string };
  transferReasons: TransferReasonCode[];
  transferExplanation: string;
  recommendedClinicians?: string;
  aiSummaryMetadata?: AiSummaryMetadata;
}

export function buildTransferQuestionnaireResponse(
  input: BuildTransferQRInput,
): QuestionnaireResponse {
  const items: QuestionnaireResponseItem[] = [
    {
      linkId: TRANSFER_QR_LINK_IDS.reasons,
      answer: input.transferReasons.map((code) => ({ valueString: code })),
    },
    {
      linkId: TRANSFER_QR_LINK_IDS.explanation,
      answer: [{ valueString: input.transferExplanation }],
    },
  ];

  if (input.recommendedClinicians?.trim()) {
    items.push({
      linkId: TRANSFER_QR_LINK_IDS.recommendedClinicians,
      answer: [{ valueString: input.recommendedClinicians.trim() }],
    });
  }

  const extensions: Extension[] = [
    { url: DETAILED_STATUS_EXT, valueCode: 'completed' },
    {
      url: EPISODE_OF_CARE_CONTEXT_EXT,
      valueReference: {
        reference: `EpisodeOfCare/${input.episodeOfCare.id}`,
        ...(input.episodeOfCare.display
          ? { display: input.episodeOfCare.display }
          : {}),
      },
    },
  ];

  if (input.aiSummaryMetadata) {
    extensions.push({
      url: AI_TRANSFER_SUMMARY_EXT,
      extension: [
        { url: 'model', valueString: input.aiSummaryMetadata.model },
        { url: 'generated-at', valueDateTime: input.aiSummaryMetadata.generatedAt },
        {
          url: 'source-counts',
          valueString: JSON.stringify(input.aiSummaryMetadata.sourceCounts),
        },
      ],
    });
  }

  const qr: QuestionnaireResponse = {
    resourceType: 'QuestionnaireResponse',
    questionnaire: input.questionnaireRef,
    status: 'completed',
    subject: {
      reference: `Patient/${input.patient.id}`,
      ...(input.patient.display ? { display: input.patient.display } : {}),
    },
    author: {
      reference: `Practitioner/${input.author.id}`,
      ...(input.author.display ? { display: input.author.display } : {}),
    },
    authored: new Date().toISOString(),
    extension: extensions,
    meta: {
      tag: [
        {
          system: TEMPLATE_CATEGORY_SYSTEM,
          code: TRANSFER_TEMPLATE_CATEGORY,
        },
      ],
    },
    item: items,
  };

  return qr;
}

// ---------------------------------------------------------------------------
// QR item extraction
// ---------------------------------------------------------------------------

export function extractTransferQrItems(qr: QuestionnaireResponse): {
  transferReasons: string[];
  transferExplanation: string | undefined;
  recommendedClinicians: string | undefined;
} {
  const reasons: string[] = [];
  let explanation: string | undefined;
  let clinicians: string | undefined;

  for (const item of qr.item ?? []) {
    switch (item.linkId) {
      case TRANSFER_QR_LINK_IDS.reasons:
        for (const ans of item.answer ?? []) {
          if (ans.valueString) reasons.push(ans.valueString);
        }
        break;
      case TRANSFER_QR_LINK_IDS.explanation:
        explanation = item.answer?.[0]?.valueString;
        break;
      case TRANSFER_QR_LINK_IDS.recommendedClinicians:
        clinicians = item.answer?.[0]?.valueString;
        break;
    }
  }

  return { transferReasons: reasons, transferExplanation: explanation, recommendedClinicians: clinicians };
}

// ---------------------------------------------------------------------------
// ServiceRequest lookup by EpisodeOfCare
// ---------------------------------------------------------------------------

/**
 * Find the ServiceRequest linked to an EpisodeOfCare via the episode-of-care extension.
 * Medplum does not index `extension:EpisodeOfCare` on ServiceRequest — search by patient
 * subject, then match the episode-of-care extension client-side.
 */
export async function findServiceRequestForEpisode(
  medplum: MedplumClient,
  episodeOfCareId: string,
  opts?: { patientId?: string },
): Promise<ServiceRequest | undefined> {
  if (!episodeOfCareId.trim()) {
    return undefined;
  }

  const patientId = await resolvePatientIdForEpisode(
    medplum,
    episodeOfCareId,
    opts?.patientId,
  );
  if (!patientId) {
    return undefined;
  }

  const results = await medplum.searchResources('ServiceRequest', {
    subject: `Patient/${patientId}`,
    _count: '50',
    _sort: '-_lastUpdated',
  });
  return pickServiceRequestForEpisode(results, episodeOfCareId);
}

/** Prefer fulfilled tracker stage when multiple SRs link to the same episode. */
export function pickLinkedServiceRequest(
  linked: ServiceRequest[],
): ServiceRequest | undefined {
  if (linked.length === 0) return undefined;
  return linked.find((sr) => getTrackerStage(sr) === 'fulfilled') ?? linked[0];
}

export function pickServiceRequestForEpisode(
  serviceRequests: ServiceRequest[],
  episodeOfCareId: string,
): ServiceRequest | undefined {
  const targetRef = episodeReference(episodeOfCareId);
  const linked = serviceRequests.filter(
    (sr) => getEpisodeOfCareRef(sr)?.reference === targetRef,
  );
  return pickLinkedServiceRequest(linked);
}

export function indexServiceRequestsByEpisodeId(
  serviceRequests: ServiceRequest[],
): Map<string, ServiceRequest> {
  const grouped = new Map<string, ServiceRequest[]>();
  for (const sr of serviceRequests) {
    const ref = getEpisodeOfCareRef(sr)?.reference;
    if (!ref?.startsWith('EpisodeOfCare/')) continue;
    const eocId = ref.slice('EpisodeOfCare/'.length);
    const list = grouped.get(eocId) ?? [];
    list.push(sr);
    grouped.set(eocId, list);
  }

  const indexed = new Map<string, ServiceRequest>();
  for (const [eocId, linked] of grouped) {
    const picked = pickLinkedServiceRequest(linked);
    if (picked) indexed.set(eocId, picked);
  }
  return indexed;
}

// ---------------------------------------------------------------------------
// Submit transfer request (coordinated 3-write)
// ---------------------------------------------------------------------------

export async function submitTransferRequest(
  medplum: MedplumClient,
  input: SubmitTransferRequestInput,
): Promise<SubmitTransferResult> {
  // 1. Resolve SR — by id or by EoC lookup
  let srId = input.serviceRequestId;
  if (!srId) {
    const found = await findServiceRequestForEpisode(medplum, input.episodeOfCareId, {
      patientId: input.patient.id,
    });
    if (!found?.id) {
      throw new Error(`No ServiceRequest found for EpisodeOfCare ${input.episodeOfCareId}`);
    }
    srId = found.id;
  }
  const sr = await medplum.readResource('ServiceRequest', srId);

  // 2. Validate
  const currentStage = getTrackerStage(sr);
  if (currentStage !== 'fulfilled') {
    throw new Error(
      `ServiceRequest ${input.serviceRequestId} is in stage '${currentStage}', expected 'fulfilled'`,
    );
  }

  const eocRef = getEpisodeOfCareRef(sr);
  if (!eocRef?.reference?.includes(input.episodeOfCareId)) {
    throw new Error(
      `ServiceRequest ${input.serviceRequestId} is not linked to EpisodeOfCare ${input.episodeOfCareId}`,
    );
  }

  if (input.transferReasons.length === 0) {
    throw new Error('At least one transfer reason is required');
  }
  if (!input.transferExplanation.trim()) {
    throw new Error('Transfer explanation is required');
  }

  // 3. Read EoC fresh (for display)
  const eoc = await medplum.readResource('EpisodeOfCare', input.episodeOfCareId);

  // 4. Resolve Questionnaire template
  const template = await getTransferQuestionnaireTemplate(medplum);
  const questionnaireRef = template
    ? `Questionnaire/${template.id}`
    : 'Questionnaire/transfer-context-template';

  // 5. Build and create QR (stamp Patient compartment refs for AccessPolicy)
  const accountRefs = await getPatientAccountRefs(medplum, input.patient.id).catch(
    () => [],
  );
  const qrDraft = buildTransferQuestionnaireResponse({
    questionnaireRef,
    patient: input.patient,
    author: input.author,
    episodeOfCare: {
      id: input.episodeOfCareId,
      display: displayLabelForEpisodeType(eoc.type?.[0]),
    },
    transferReasons: input.transferReasons,
    transferExplanation: input.transferExplanation,
    recommendedClinicians: input.recommendedClinicians,
    aiSummaryMetadata: input.aiSummaryMetadata,
  });
  if (accountRefs.length > 0) {
    qrDraft.meta = {
      ...qrDraft.meta,
      accounts: accountRefs,
    };
  }
  const savedQr = await medplum.createResource(qrDraft);

  // 6. Update SR: stage + preferences
  const srCopy: ServiceRequest = JSON.parse(JSON.stringify(sr));
  setTrackerStage(srCopy, 'transfer');

  if (input.sessionFormatPreference) {
    setSessionFormatPreference(srCopy, input.sessionFormatPreference);
  }
  if (input.availabilityPreference) {
    setAvailabilityPreference(srCopy, input.availabilityPreference);
  }
  if (input.areasOfFocus) {
    setAreasOfFocus(srCopy, input.areasOfFocus);
  }
  if (input.providerPreferences) {
    setProviderPreferences(srCopy, input.providerPreferences);
  }

  const savedSr = await medplum.updateResource(srCopy);

  return { serviceRequest: savedSr, questionnaireResponse: savedQr };
}

// ---------------------------------------------------------------------------
// SR preference extraction (for display in transfer detail views)
// ---------------------------------------------------------------------------

export function extractServiceRequestPreferences(sr: ServiceRequest): {
  sessionFormat: string | undefined;
  availability: string[] | undefined;
  areasOfFocus: string[] | undefined;
  providerPreferences: string[] | undefined;
} {
  return {
    sessionFormat: srGetSessionFormatPreference(sr),
    availability: srGetAvailabilityPreference(sr),
    areasOfFocus: srGetAreasOfFocus(sr),
    providerPreferences: srGetProviderPreferences(sr),
  };
}
